import { ApplicationElement, html } from '@crossiety/lit-components';
import '@crossiety/lit-components/components/grid_list/element.js';
import '@crossiety/lit-components/components/icon/element.js';

import translations from './translations.js';
import style from './style.lit.css';
import '../attachment/element.js';

/**
 * A file upload wrapper responsible for handling upload, attachment and removal logic.
 *
 * @prop type - Type of the files being uploaded (options: image, attachment)
 * @prop disabled - Whether the file upload is disabled, will be set automatically by reading if slotted file input is disabled
 *
 * @cssprop [--image-size: 50px] Width and height of uploaded and attached images
 * @cssprop [--remove-button-size: 24px] Width and height of remove buttons
 * @cssprop [--remove-button-color: var(--dark-text-secondary)] Text color of remove buttons
 * @cssprop [--remove-button-bg: var(--contrast-light)] Background color of remove buttons
 * @cssprop [--remove-button-fs: var(--font-size-base)] Font size of remove buttons
 * @cssprop [--big-button-margin: 0 8px] Margins of upload/clear button
 * @cssprop [--big-button-padding: 8px] Paddings of upload/clear button
 * @cssprop [--big-button-bg: var(--contrast-light)] Background color of upload/clear button
 * @cssprop [--big-button-fs: var(--font-size-sm)] Font size of upload/clear button
 * @cssprop [--big-button-border: 1px solid var(--input-border)] Upload/clear button border
 * @cssprop [--big-button-icon-size: var(--font-size-base)] Font size of upload/clear buttons icon
 * @cssprop [--upload-button-color: var(--dark-text)] Text color of upload button
 * @cssprop [--clear-button-color: var(--danger)] Text color of clear button
 * @cssprop [--attached-file-shadow: 0 0 8px var(--primary)] Shadow of all new (attached) files
 * @cssprop [--file-border: 1px solid red: none] Border of all files (new and old) - can be used to show invalid state
 * @cssprop [--xs-attachment-border-radius: 6px] Border radius of all xs-attachments, best used in combination with --file-border
 *
 * @slot default - List of img (when type = image) or xs-attachment (when type = attachment) elements. Each element is expected to have unique `data-id`/`data-cache-id` property.
 * @slot inputs - List of 2 inputs (one type = file, other type = hidden). Component will set the file list of file upload input and value of hidden input which will look something like this: value="1,10,133"
 * @slot cache-inputs - Cached files are expected to have a `data-cache-id` property, and a hidden input sibling whose id matches the value of data-cache-id. Those inputs go here. Value of each input contains data about the cached file.
 * @slot upload-button - Upload button
 *
 * @part btn-remove All the remove buttons
 * @part btn-upload - Upload button
 * @part list-top - Wrapper containing uploaded and attached files
 * @part list-bottom - Wrapper containing: upload button and clear attachments button
 *
 * @event fileUpload-sizeChange - Attached file has been added/removed, you can access the current size via `e.target.attachedSize`.
 */

export default class FileUpload extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    type: { type: String },
    disabled: { type: Boolean, reflect: true },
    removedIds: { type: Array, state: true },
    uploaded: { type: Array, state: true },
    attached: { type: Array, state: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.type = 'image';
    this.removedIds = [];
    this.uploaded = [];
    this.attached = [];
    this.createdUrls = []; // Urls created by the component, should be revoked or they will leak memory
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.createdUrls.forEach(URL.revokeObjectURL);
  }

  willUpdate(changedProps) {
    super.willUpdate(changedProps);

    // this.fileUploadInput makes sure the component is fully loaded into DOM
    if (changedProps.has('attached') && this.fileUploadInput)
      this.dispatchEvent(
        new Event('fileUpload-sizeChange', {
          bubbles: true,
          composed: true,
        })
      );
  }

  /** @internal */
  get isImage() {
    return this.type === 'image';
  }

  /** @internal */
  get slottedInputs() {
    const slot = this.shadowRoot.querySelector('slot[name="inputs"]');
    return slot?.assignedElements({ flatten: true }) || [];
  }

  /** @internal */
  get uploadedToShow() {
    return this.uploaded.filter(u => !this.removedIds.includes(u.dataset.id));
  }

  /** @internal */
  get fileUploadInput() {
    return this.slottedInputs.find(input => input.type === 'file');
  }

  /** @internal */
  get removedIdsInput() {
    return this.slottedInputs.find(input => input.type === 'hidden');
  }

  /** @internal */
  get isSingleFileInput() {
    return !this.fileUploadInput?.multiple;
  }

  /** @internal */
  get shouldDisableUpload() {
    return (
      this.isSingleFileInput &&
      this.attached.length + this.uploadedToShow.length
    );
  }

  /** @internal */
  get supportsDataTransferItems() {
    // Safari 10 is the only supported browser that does not support DataTransfer().items API which is used for removing single attachment.
    // In the case browser doesn't support it, we will enable the option to bulk remove all attachments (supported) instead.
    // TODO: Remove the whole logic when we stop supporting Safari 10
    try {
      return typeof new DataTransfer().items.add === 'function';
    } catch {
      return false;
    }
  }

  get attachedSize() {
    return this.attached.reduce((totalSize, file) => totalSize + file.size, 0);
  }

  onValuesSlotChange(e) {
    this.uploaded = e.target.assignedElements({ flatten: true });
  }

  onInputsSlotChange(e) {
    const inputs = e.target.assignedElements({ flatten: true });
    this.disabled = inputs.some(
      input => input.type === 'file' && input.disabled
    );
  }

  onUploaderClick() {
    this.fileUploadInput.click();
  }

  onInput(e) {
    const input = e.target;
    if (input.type !== 'file') return;

    this.attached = Array.from(input.files);
  }

  removeUploaded(file) {
    if (file.dataset.cacheId) {
      // Remove hidden input value for remotely cached files
      this.querySelector(`#${file.dataset.cacheId}`).remove();
      this.uploaded = this.uploaded.filter(
        i => i.dataset.cacheId !== file.dataset.cacheId
      );
    } else {
      this.removedIds = [...this.removedIds, file.dataset.id];
      this.removedIdsInput.value = String(this.removedIds);
    }
  }

  removeAttached(e) {
    const index = +e.currentTarget.dataset.index;
    this.attached = this.attached.filter((_, i) => i !== index);
    const dataTransfer = new DataTransfer();
    this.attached.forEach(file => {
      dataTransfer.items.add(file);
    });
    this.fileUploadInput.files = dataTransfer.files;
  }

  clearAttached() {
    this.attached = [];
    const { fileUploadInput } = this;
    // The next 3 lines are a supported way of resetting a file inputs "files" property
    fileUploadInput.type = 'text';
    fileUploadInput.value = null;
    fileUploadInput.type = 'file';
  }

  renderImgElement({ src, alt }) {
    return html`<img src=${src} alt=${alt} />`;
  }

  renderAttachmentElement({ file, href }) {
    return html`
      <xs-attachment file=${file} href=${href} layout="lg"> </xs-attachment>
    `;
  }

  renderUploadedFile(file) {
    const hasError = file.dataset.errors === 'true';

    return html`
      <div class="uploaded${hasError ? ' error' : null}">
        ${this.isImage
          ? this.renderImgElement(file)
          : this.renderAttachmentElement(file)}
        <button
          type="button"
          part="btn-remove"
          class="btn-remove"
          aria-label=${this.t('remove')}
          @click=${() => this.removeUploaded(file)}>
          <ef-icon name="close"></ef-icon>
        </button>
      </div>
    `;
  }

  renderAttachedFile(file, index) {
    const url = URL.createObjectURL(file);
    this.createdUrls.push(url);

    return html`
      <div class="attached">
        ${this.isImage
          ? this.renderImgElement({
              src: url,
              alt: this.t('recently_attached'),
            })
          : this.renderAttachmentElement({
              file: file.name,
              href: url,
            })}
        <button
          type="button"
          part="btn-remove"
          class="btn-remove"
          aria-label=${this.t('remove')}
          ?hidden=${!this.supportsDataTransferItems}
          data-index=${index}
          @click=${this.removeAttached}>
          <ef-icon name="close"></ef-icon>
        </button>
      </div>
    `;
  }

  render() {
    return html`
      <slot hidden @slotchange=${this.onValuesSlotChange}></slot>
      <slot hidden name="cache-inputs"></slot>
      <slot
        hidden
        name="inputs"
        @slotchange=${this.onInputsSlotChange}
        @input=${this.onInput}>
      </slot>

      <ef-grid-list col="0" gap="large" part="list-top">
        ${this.uploadedToShow?.map(f => this.renderUploadedFile(f))}
        ${this.attached.map((f, i) => this.renderAttachedFile(f, i))}
      </ef-grid-list>
      <ef-grid-list col="0" part="list-bottom">
        <slot
          ?hidden=${this.shouldDisableUpload}
          name="upload-button"
          @click=${this.onUploaderClick}>
          <button type="button" part="btn-upload" class="btn-upload">
            <ef-icon name=${this.isImage ? 'file-image' : 'upload'}></ef-icon>
            ${this.t('upload')}
          </button>
        </slot>
        <slot
          ?hidden=${!this.attached.length ||
          !this.supportsDataTransferItems ||
          this.isSingleFileInput}
          name="btn-clear"
          @click=${this.clearAttached}>
          <button type="button" part="btn-clear" class="btn-clear">
            <ef-icon name="close"></ef-icon>
            ${this.t('clear_attached')}
          </button>
        </slot>
      </ef-grid-list>
    `;
  }
}

FileUpload.registerTagName('xs-file-upload');

customElements.get('xs-file-upload') ||
  customElements.define('xs-file-upload', FileUpload);
